import { useEffect, useMemo, useState } from 'react';
import {
  CLAVE_NOTAS_RAPIDAS,
  LIMITE_NOTA,
  componerNota,
  leerNotasRapidas,
  separarNota,
  type GrupoNotasRapidas,
} from '@shared/notasRapidas';

/**
 * Sprint 138 — Cómo lo quiere el cliente, para UN producto.
 *
 * Botones primero, teclado después. En hora pico el término se toca; lo que no está en la lista
 * se escribe. La lista la configura el negocio (Configuración → Cómo se pide la carne) y se lee
 * una sola vez por sesión de pantalla.
 *
 * Los botones son de 44 px y con texto legible a 60 cm — esto se usa de pie, con el cliente
 * enfrente, no con un mouse.
 */

/**
 * Carga la lista configurada.
 *
 * `habilitado = false` para la página pública del cliente: ahí no hay sesión y el bridge
 * respondería 401. En ese caso la lista llega dentro de la respuesta del menú y se pasa por prop.
 */
export function useNotasRapidas(habilitado = true): GrupoNotasRapidas[] {
  const [grupos, setGrupos] = useState<GrupoNotasRapidas[]>(() => leerNotasRapidas(null));
  useEffect(() => {
    if (!habilitado) return;
    let vivo = true;
    void (async () => {
      try {
        const res = await window.api.config.obtener(CLAVE_NOTAS_RAPIDAS);
        if (vivo && res.ok) setGrupos(leerNotasRapidas(res.data));
      } catch {
        /* La lista de fábrica ya está puesta: nunca se queda sin botones por un error de red. */
      }
    })();
    return () => {
      vivo = false;
    };
  }, [habilitado]);
  return grupos;
}

interface Props {
  abierto: boolean;
  /** Qué producto se está anotando; va en el título para no perder el contexto. */
  nombreProducto: string;
  notaActual?: string | null;
  onGuardar: (nota: string) => void;
  onCerrar: () => void;
  /** Los grupos ya cargados. Se pasan desde fuera para no consultar una vez por renglón. */
  grupos?: GrupoNotasRapidas[];
  /**
   * Sprint 139 — Una advertencia honesta cuando la nota vale para varias unidades.
   *
   * En el POS, anotar separa el renglón; en el menú del cliente el carrito no tiene renglones, y
   * callarlo haría que alguien escribiera "término medio" creyendo que aplica a una sola de las
   * dos piezas que lleva.
   */
  aviso?: string | null;
  /** `true` en el menú en línea: ahí quien lee es el cliente, no quien atiende. */
  tuteaAlCliente?: boolean;
}

export function NotaProductoModal({
  abierto,
  nombreProducto,
  notaActual,
  onGuardar,
  onCerrar,
  grupos: gruposProp,
  aviso,
  tuteaAlCliente = false,
}: Props) {
  /*
    Sólo se consulta cuando el modal está abierto de verdad: el componente se monta con cada
    renglón del carrito y con el modificador de paquete, y una consulta por montaje es una
    petición al servidor por cada vez que alguien abre un paquete sin tocar las notas.
  */
  const gruposHook = useNotasRapidas(gruposProp === undefined && abierto);
  const grupos = gruposProp ?? gruposHook;

  const inicial = useMemo(() => separarNota(notaActual, grupos), [notaActual, grupos]);
  const [seleccion, setSeleccion] = useState<string[]>(inicial.seleccion);
  const [textoLibre, setTextoLibre] = useState(inicial.textoLibre);

  /**
   * Al abrir sobre otro renglón hay que re-sembrar: el modal se reutiliza entre productos.
   *
   * Sprint 139 — **`grupos` NO va en las dependencias.** Cuando estaba, una lista que llegaba
   * tarde del servidor volvía a sembrar el estado y **borraba la selección y el texto que el
   * operador acababa de escribir**: intermitente, silencioso, y se lee como *"el sistema me borró
   * lo que puse"*. Lo que se re-siembra es la nota del renglón, y eso sólo depende del renglón.
   */
  useEffect(() => {
    if (!abierto) return;
    const s = separarNota(notaActual, grupos);
    setSeleccion(s.seleccion);
    setTextoLibre(s.textoLibre);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto, notaActual]);

  if (!abierto) return null;

  const alternar = (opcion: string) =>
    setSeleccion((prev) =>
      prev.includes(opcion) ? prev.filter((o) => o !== opcion) : [...prev, opcion],
    );

  const nota = componerNota(seleccion, textoLibre);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4">
      <div className="flex max-h-[90vh] w-full flex-col rounded-t-2xl bg-white shadow-xl sm:max-w-lg sm:rounded-2xl">
        <header className="border-b border-slate-200 px-5 py-3">
          {/*
            Sprint 140 — La pregunta cambia según quién la lee.

            En el mostrador la lee quien atiende y habla del cliente; en el menú en línea la lee
            **el cliente**, y encontrarse con *"¿Cómo lo quiere el cliente?"* en su propio celular
            es el sistema hablando de él en tercera persona. Detectado al recorrer el menú público
            desde un teléfono.
          */}
          <h2 className="text-lg font-semibold">
            {tuteaAlCliente ? '¿Cómo lo quieres?' : '¿Cómo lo quiere el cliente?'}
          </h2>
          <p className="text-sm text-slate-600">{nombreProducto}</p>
        </header>

        <div className="flex-1 space-y-4 overflow-auto px-5 py-4">
          {aviso && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900">
              {aviso}
            </p>
          )}
          {grupos.map((g) => (
            <div key={g.titulo}>
              <h3 className="mb-2 text-sm font-semibold text-slate-700">{g.titulo}</h3>
              <div className="flex flex-wrap gap-2">
                {g.opciones.map((o) => {
                  const activo = seleccion.includes(o);
                  return (
                    <button
                      key={o}
                      type="button"
                      onClick={() => alternar(o)}
                      aria-pressed={activo}
                      className={`min-h-[44px] rounded-lg border-2 px-4 text-base font-medium transition ${
                        activo
                          ? 'border-orange-600 bg-orange-600 text-white'
                          : 'border-slate-300 bg-white text-slate-800 hover:bg-slate-50'
                      }`}
                    >
                      {o}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <div>
            <label className="label" htmlFor="notaLibre">
              Algo más (opcional)
            </label>
            <textarea
              id="notaLibre"
              className="input"
              rows={2}
              value={textoLibre}
              onChange={(e) => setTextoLibre(e.target.value)}
              placeholder="ej. sin hueso, que le quede jugosa…"
              maxLength={LIMITE_NOTA}
            />
          </div>

          {/*
            Lo que va a salir impreso, con las mismas palabras. Si el operador no puede ver la
            nota final antes de guardar, la descubre cuando ya está en la parrilla.
          */}
          {nota && (
            <div className="rounded-lg border-l-4 border-orange-500 bg-orange-50 px-3 py-2">
              <p className="text-xs font-semibold uppercase text-orange-800">Va a salir así</p>
              <p className="text-base font-bold text-orange-900">{nota}</p>
            </div>
          )}
        </div>

        <footer className="flex gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
          <button type="button" onClick={onCerrar} className="btn-ghost min-h-[44px] flex-1">
            Cancelar
          </button>
          {/*
            Quitar la nota es una acción distinta de guardarla vacía, y tiene que estar a la vista:
            "ya no la quiere así" pasa tanto como "la quiere así".
          */}
          {notaActual ? (
            <button
              type="button"
              onClick={() => {
                onGuardar('');
                onCerrar();
              }}
              className="btn-ghost min-h-[44px] flex-1 text-red-600"
            >
              Quitar nota
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => {
              onGuardar(nota);
              onCerrar();
            }}
            className="btn-primary min-h-[44px] flex-1"
          >
            Guardar
          </button>
        </footer>
      </div>
    </div>
  );
}
